/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container } from 'react-bootstrap';
import { Alert } from 'react-bootstrap';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import Header from './components/Header/Header';
import Home from './components/Home';
import Status from './components/Status';
import Queues from './components/Queues';
import QueueDetail from './components/QueueDetail';
import Domains from './components/Domains';
import Vmtas from './components/Vmtas';
import Jobs from './components/Jobs';
import JobDetail from './components/JobDetail';
import DomainDetail from './components/DomainDetail';
import Logs from './components/Logs';
import Command from './components/Command';
import Lak from './components/Lak';
import Config from './components/Config';
import AutoRefresh from './components/AutoRefresh';
import './App.css';

var homepage = require('../package.json').homepage;

export const AlertLevel = {
    // See https://react-bootstrap.github.io/components/alerts/
    DANGER: "danger",
    INFO: "info",
    SUCCESS: "success"
}

/*
** Encapsulates entire page of the web monitor.
*/
export default class App extends Component {
    constructor(props) {
        super(props)
        this.state = {}
        this.refresh = this.refresh.bind(this);
        this.refreshInterval = this.refreshInterval.bind(this);
        this.setRefresh = this.setRefresh.bind(this);
        this.setRefreshInterval = this.setRefreshInterval.bind(this);
        this.showError = this.showError.bind(this);
        this.makeQueues = this.makeQueues.bind(this);
        this.makeQueueDetail = this.makeQueueDetail.bind(this);
        this.makeDomainDetail = this.makeDomainDetail.bind(this);
        this.makeJobDetail = this.makeJobDetail.bind(this);
    }


    /**
     * Shows the given error message.
     *
     * @param message
     *      what message to show.
     */
    showError(message, level = AlertLevel.DANGER) {
        this.setState({
            alert: {
                message: message,
                level: level
            }
        })
    }


    /**
     * Makes a component for queues with the desired props, mainly the
     * prop to set the "queue" name (if any, from the URL path parameters)
     * and the callback for showing the error messages, if any.
     *
     * @return
     *      queues component with the desired props.
     */
    makeQueues(props) {
        if (Object.keys(props.match.params).length > 0) {
            let domain = props.match.params.domain
            let vmta = props.match.params.vmta
            let queue = domain + "/" + vmta
            return <Queues queue={queue} setRefresh={this.setRefresh}
                           showError={this.showError}
                           setRefreshInterval={this.setRefreshInterval}/>
        }
        return <Queues setRefresh={this.setRefresh}
                       showError={this.showError}
                       setRefreshInterval={this.setRefreshInterval}/>
    }


    /**
     * Makes a component for queue detail with the desired props, mainly the
     * prop to set the "queue" name and the callback for showing the
     * error messages, if any.
     *
     * @return
     *      queue detail component with the desired props.
     */
    makeQueueDetail(props) {
        let domain = props.match.params.domain
        let vmta = props.match.params.vmta
        let queue = domain + "/" + vmta
        return <QueueDetail queue={queue} setRefresh={this.setRefresh}
                            showError={this.showError}
                            setRefreshInterval={this.setRefreshInterval}/>
    }


    /**
     * Makes a component for domain detail with the desired props, mainly the
     * prop to set the "domain" name and the callback for showing the
     * error messages, if any.
     *
     * @return
     *      domain detail component with the desired props.
     */
    makeDomainDetail(props) {
        let domain = props.match.params.domain
        return <DomainDetail domain={domain} setRefresh={this.setRefresh}
                             showError={this.showError}
                             setRefreshInterval={this.setRefreshInterval}/>
    }


    /**
     * Makes a component for job detail with the desired props, mainly the
     * prop to set the "job" ID and the callback for showing the
     * error messages, if any.
     *
     * @return
     *      job detail component with the desired props.
     */
    makeJobDetail(props) {
        let job = props.match.params.job
        return <JobDetail job={job} setRefresh={this.setRefresh}
                          showError={this.showError}
                          setRefreshInterval={this.setRefreshInterval}/>
    }


    /**
     * Refreshes the component that is currently shown.
     */
    refresh() {
        // this.state.refresh should be always set in production to the
        // function that refreshes the page's component mounted currently.
        // However in development, you may visit the web root without the
        // "homepage" in URL and this results in no page component being
        // mounted (see the routes in BrowserRouter below), and hence in this
        // case the this.state.refresh may not be set.
        this.state.refresh && this.state.refresh()
    }


    /**
     * Returns the interval (in milliseconds) at which the component needs to be refreshed.
     */
    refreshInterval() {
        return this.state.refreshInterval ? this.state.refreshInterval() : undefined
    }


    /**
     * Sets what to invoke to refresh the page.
     * 
     * @param newRefresh
     *      function to invoke to refresh the page.
     *
     *      If a component does not need to be refreshed automatically, then
     *      set this to <code>undefined</code> to disable auto-refresh. 
     *      For such components, the auto-refresh buttons are not shown.
     */
    setRefresh(refresh) {
        this.setState({
            refresh: refresh
        })
    }


    /**
     * Sets how to get the interval at which to refresh the page.
     * 
     * @param refreshInterval
     *      function to invoke to get the refresh interval.
     *
     *      If a component does not need to be refreshed automatically, then
     *      set this to <code>undefined</code>.
     */
    setRefreshInterval(refreshInterval) {
        this.setState({
            refreshInterval: refreshInterval
        })
    }
    

    render() {
        return (
            <div className="App">
                <BrowserRouter>
                    <div>
                        <Header showError={this.showError}/>
                        <Container>
                            { this.state.refresh &&
                                <AutoRefresh refresh={this.refresh}
                                             interval={this.refreshInterval}/> }
                        </Container>
                        <div className='clearFloat'/>
                        {
                            this.state.alert && this.state.alert.message &&
                            // dangerouslySetInnerHTML is generally not recommended as
                            // it makes the code vulnerable to XSS.  But in this case
                            // we are setting response from PMTA (which we trust).
                            // See https://reactjs.org/docs/dom-elements.html
                            <Alert variant={this.state.alert.level}>
                                <div dangerouslySetInnerHTML={{__html: this.state.alert.message}}/>
                            </Alert>
                        }
                        <Switch>
                            <Route path={homepage + "/"} exact 
                                render={() => 
                                    <Home setRefresh={this.setRefresh}
                                          showError={this.showError}
                                          setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/status"} exact 
                                render={() => 
                                    <Status setRefresh={this.setRefresh}
                                            showError={this.showError}
                                            setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/queues"} exact 
                                render={this.makeQueues}/>
                            <Route path={homepage + "/queues/:domain/:vmta"}
                                render={this.makeQueues}/>
                            <Route path={homepage + "/queueDetail/:domain/:vmta"}
                                render={this.makeQueueDetail}/>
                            <Route path={homepage + "/domains"} exact 
                                render={() => 
                                    <Domains setRefresh={this.setRefresh}
                                             showError={this.showError}
                                             setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/domainDetail/:domain"}
                                render={this.makeDomainDetail}/>
                            <Route path={homepage + "/vmtas"} exact 
                                render={() => 
                                    <Vmtas setRefresh={this.setRefresh}
                                           showError={this.showError}
                                           setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/jobs"} exact 
                                render={() => 
                                    <Jobs setRefresh={this.setRefresh}
                                          showError={this.showError}
                                          setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/jobDetail/:job"} exact 
                                render={this.makeJobDetail}/>
                            <Route path={homepage + "/logs"} exact 
                                render={() => 
                                    <Logs setRefresh={this.setRefresh}
                                          showError={this.showError}
                                          setRefreshInterval={this.setRefreshInterval}/>}/>
                            <Route path={homepage + "/command"} exact 
                                render={() => 
                                    <Command setRefresh={this.setRefresh}
                                             showError={this.showError}/>}/>
                            <Route path={homepage + "/enterLak"} exact 
                                render={() => 
                                    <Lak setRefresh={this.setRefresh}
                                         showError={this.showError}/>}/>
                            <Route path={homepage + "/config"} exact 
                                render={() => 
                                    <Config setRefresh={this.setRefresh}
                                            showError={this.showError}/>}/>
                        </Switch>
                    </div>
                </BrowserRouter>
            </div>
        );
    }
}
